import { ChevronRight } from 'lucide-react';

import { cn } from '@/shared/utils/cn';

interface Props {
  children?: string[] | null;
  maxVisible?: number;
  onDrillDown?: () => void;
  isDisabled?: boolean;
  className?: string;
  itemType?: 'brand' | 'product';
}

export function ChildrenCell({
  children,
  maxVisible = 2,
  onDrillDown,
  isDisabled = false,
  className,
  itemType = 'brand',
}: Props) {
  // No children or empty array
  if (!children || children.length === 0) {
    return (
      <div className={cn('flex items-center', className)}>
        <div className="flex min-h-[40px] flex-1 items-center gap-2 rounded-md border border-transparent bg-transparent px-3 py-2 text-sm opacity-50">
          <div className="flex flex-1 flex-col items-start gap-1 leading-none">
            <div className="text-sm leading-none font-medium">-</div>
          </div>
        </div>
      </div>
    );
  }

  const visibleItems = children.slice(0, maxVisible);
  const remainingCount = children.length - maxVisible;
  const hasMore = remainingCount > 0;

  const countText =
    children.length === 1 ? `1 ${getItemTypeName()}` : `${children.length} ${getItemTypeName()}s`;

  return (
    <div className={cn('flex items-center', className)}>
      <div
        className={cn(
          'flex min-h-[40px] flex-1 items-center gap-2 rounded-md border px-3 py-2 text-sm',
          isDisabled
            ? 'cursor-not-allowed border-transparent bg-transparent opacity-50'
            : 'border-border cursor-pointer bg-white hover:bg-gray-50',
        )}
        onClick={
          isDisabled
            ? undefined
            : (event) => {
                event.stopPropagation();
                onDrillDown?.();
              }
        }
      >
        <div className="flex flex-1 flex-col items-start gap-1 leading-none">
          <div className="text-sm leading-none font-medium">{countText}</div>
          <div className="text-muted-foreground flex gap-1 text-xs leading-none">
            <div className="max-w-[120px] truncate">{visibleItems.join(', ')}</div>
            <div className="flex-1">{hasMore && ` +${remainingCount}`}</div>
          </div>
        </div>
        {!isDisabled && <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />}
      </div>
    </div>
  );

  function getItemTypeName(): string {
    return itemType;
  }
}
